import { useMemo, useState } from 'react';
import { Card, ConfirmModal, Modal } from '@safetyhub/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRightIcon } from './Icons';
import { extractApiError } from '../../../../lib/api';
import {
  SiteContact,
  getSiteContacts,
  createSiteContact,
  updateSiteContact,
  deleteSiteContact,
} from '../../../../lib/tbm';
import { SiteContactFormModal, SiteContactFormValue } from '../../../../components/sites/SiteContactFormModal';
import { MemberPhoneConflictModal } from '../../../../components/sites/MemberPhoneConflictModal';
import { useSiteContactCreator } from '../../../../hooks/useSiteContactCreator';
import { useToastStore } from '../../../../stores/toast';
import { Combobox } from '../../../../components/common/Combobox';
import { EditModalFooter } from '../../../../components/common/EditModalFooter';

interface SiteContactsSectionProps {
  companyId: string;
  siteId: string;
  isOwner: boolean;
}

/** Member 단위 그룹 (같은 memberId의 SiteContact 배열) */
interface MemberGroup {
  memberId: string;
  name: string;
  phone: string;
  email?: string;
  contacts: SiteContact[];
}

/** 역할 summary inline 텍스트 (부서/직책, ...) */
function buildRoleSummary(contacts: SiteContact[]): string {
  return contacts
    .map((c) => {
      if (c.department && c.position) return `${c.department}/${c.position}`;
      if (c.department) return c.department;
      if (c.position) return c.position;
      return '(역할 미지정)';
    })
    .join(', ');
}

/** contacts 배열 → Map<memberId, MemberGroup> */
function groupByMember(contacts: SiteContact[]): MemberGroup[] {
  const map = new Map<string, MemberGroup>();
  for (const c of contacts) {
    const existing = map.get(c.memberId);
    if (existing) {
      existing.contacts.push(c);
    } else {
      map.set(c.memberId, {
        memberId: c.memberId,
        name: c.name,
        phone: c.phone,
        email: c.email,
        contacts: [c],
      });
    }
  }
  return Array.from(map.values());
}

// ──────────────────────────────────────────────────────────────────────────────
// 역할 추가/수정 form (부서/직책만 입력)
// ──────────────────────────────────────────────────────────────────────────────
interface RoleFormProps {
  title: string;
  /** 수정 모드 — 무변경 저장을 막고 미저장 닫기를 확인한다(추가 모드는 기존 동작 유지). */
  isEdit?: boolean;
  initialDepartment?: string;
  initialPosition?: string;
  departmentSuggestions?: string[];
  onSubmit: (department: string, position: string) => Promise<void>;
  onCancel: () => void;
}

/**
 * 수정 모드 푸터 — 취소가 base Modal의 requestClose로 수렴해야 dirty 닫기 확인을 탄다.
 * (requestClose context는 Modal children **하위 컴포넌트**에서만 잡히므로 분리했다 — base Modal 규약)
 */
function RoleFormFooter({
  isEdit,
  isDirty,
  isSubmitting,
  onFallbackClose,
  onSave,
}: {
  isEdit: boolean;
  isDirty: boolean;
  isSubmitting: boolean;
  onFallbackClose: () => void;
  onSave: () => void;
}) {
  return (
    <EditModalFooter
      isDirty={isDirty}
      showHint={isEdit}
      submitLabel="저장"
      submitDisabled={isSubmitting || (isEdit && !isDirty)}
      busy={isSubmitting}
      onSubmit={onSave}
      onFallbackClose={onFallbackClose}
    />
  );
}

function RoleForm({
  title,
  isEdit = false,
  initialDepartment = '',
  initialPosition = '',
  departmentSuggestions = [],
  onSubmit,
  onCancel,
}: RoleFormProps) {
  const [department, setDepartment] = useState(initialDepartment);
  const [position, setPosition] = useState(initialPosition);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputClass =
    'w-full px-3 py-2 border border-line rounded-control text-sm focus:ring-1 focus:ring-brand-400 focus:outline-none disabled:bg-surface-soft';

  // 편집 입력 전수(부서·직책)를 진입 시점 값과 비교한다 — 두 필드뿐이라 스냅샷 훅 없이 직접 비교.
  const isDirty =
    department.trim() !== initialDepartment.trim() ||
    position.trim() !== initialPosition.trim();

  async function handleSubmit() {
    // 버튼 disabled를 우회하는 경로(직책 입력 Enter)까지 무변경 저장을 막는다.
    if (isEdit && !isDirty) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(department.trim(), position.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  }

  // MemberDetailModal(z-[70]) 위에 겹치는 sub — 오버레이·Esc·포커스 트랩은 base가 소유한다.
  return (
    <Modal
      open
      onClose={onCancel}
      title={title}
      size="sm"
      overlayZClassName="z-[80]"
      isDirty={isEdit && isDirty}
      footer={
        <RoleFormFooter
          isEdit={isEdit}
          isDirty={isDirty}
          isSubmitting={isSubmitting}
          onFallbackClose={onCancel}
          onSave={handleSubmit}
        />
      }
    >
      {error && (
        <div className="mb-3 rounded-card bg-red-50 p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">담당부서</label>
          <Combobox
            value={department}
            onChange={setDepartment}
            suggestions={departmentSuggestions}
            placeholder="예: 안전관리팀"
            disabled={isSubmitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">직책</label>
          {/* Enter=저장은 이 입력에만. Esc는 base가 document 레벨에서 처리한다. */}
          <input
            type="text"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            placeholder="예: 현장소장"
            disabled={isSubmitting}
            className={inputClass}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); }
            }}
          />
        </div>
      </div>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// 삭제 확인 모달 (sub modal z-[80])
// ──────────────────────────────────────────────────────────────────────────────
interface DeleteConfirmProps {
  contact: SiteContact;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteConfirm({ contact, isDeleting, onConfirm, onCancel }: DeleteConfirmProps) {
  const roleLabel = contact.department || contact.position
    ? [contact.department, contact.position].filter(Boolean).join('/')
    : '(역할 미지정)';

  // 파괴적 확인이라 danger — 규약대로 **취소가 default(자동 포커스)** 다.
  return (
    <ConfirmModal
      open
      title="역할 삭제"
      body={
        <p className="text-sm text-ink-500">
          <strong>{roleLabel}</strong> 역할을 삭제하시겠습니까?
        </p>
      }
      confirmLabel={isDeleting ? '삭제 중...' : '삭제'}
      danger
      busy={isDeleting}
      overlayZClassName="z-[80]"
      onConfirm={onConfirm}
      onClose={onCancel}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Member 상세 모달 (메인 모달 z-[70])
// ──────────────────────────────────────────────────────────────────────────────
interface MemberDetailModalProps {
  group: MemberGroup;
  isOwner: boolean;
  companyId: string;
  siteId: string;
  departmentSuggestions: string[];
  onClose: () => void;
  onRefresh: () => void;
}

function MemberDetailModal({
  group,
  isOwner,
  companyId,
  siteId,
  departmentSuggestions,
  onClose,
  onRefresh,
}: MemberDetailModalProps) {
  // sub modal 상태
  type SubModal =
    | { type: 'add-role' }
    | { type: 'edit-role'; contact: SiteContact }
    | { type: 'edit-info' }
    | { type: 'delete-role'; contact: SiteContact };

  const [subModal, setSubModal] = useState<SubModal | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleAddRole = async (department: string, position: string) => {
    await createSiteContact(companyId, siteId, {
      name: group.name,
      phone: group.phone,
      memberId: group.memberId,
      department: department || undefined,
      position: position || undefined,
    });
    onRefresh();
    setSubModal(null);
  };

  const handleEditRole = async (department: string, position: string) => {
    if (subModal?.type !== 'edit-role') return;
    await updateSiteContact(companyId, siteId, subModal.contact.id, {
      department: department || undefined,
      position: position || undefined,
    });
    onRefresh();
    setSubModal(null);
  };

  const handleEditInfo = async (value: SiteContactFormValue) => {
    // 임의의 첫 번째 SiteContact id로 Member 정보 동기화
    const firstContact = group.contacts[0];
    await updateSiteContact(companyId, siteId, firstContact.id, {
      name: value.name,
      phone: value.phone,
      email: value.email,
    });
    onRefresh();
    setSubModal(null);
  };

  const handleDeleteRole = async () => {
    if (subModal?.type !== 'delete-role') return;
    setIsDeleting(true);
    try {
      await deleteSiteContact(companyId, siteId, subModal.contact.id);
      onRefresh();
      // 역할이 모두 삭제되면 모달 닫기
      if (group.contacts.length <= 1) {
        onClose();
      } else {
        setSubModal(null);
      }
    } catch (err) {
      console.error('Failed to delete site contact:', extractApiError(err).message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Modal
        open
        onClose={onClose}
        ariaLabel={`${group.name} 담당자 상세`}
        size="md"
        // 헤더 제목 자리는 아바타·연락처·[정보 수정]이 차지하는 커스텀 블록이라 title은 비운다
        // (base 헤더는 ✕만 남는다 — aria 이름은 ariaLabel이 준다).
        footer={
          <div className="flex w-full justify-between items-center">
            {isOwner ? (
              <button
                onClick={() => setSubModal({ type: 'add-role' })}
                className="text-sm text-brand-600 hover:text-brand-800"
              >
                + 역할 추가
              </button>
            ) : (
              <span />
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-ink-700 bg-white border border-line rounded-card-sm hover:bg-surface-hover"
            >
              닫기
            </button>
          </div>
        }
      >
        {/* Member 정보 영역 */}
        <div className="flex items-center mb-4">
          <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <span className="text-green-700 font-medium text-lg">{group.name.charAt(0)}</span>
          </div>
          <div className="ml-4 flex-1 min-w-0">
            <h3 className="text-lg font-medium text-ink-900 truncate">{group.name}</h3>
            <p className="text-sm text-ink-500 truncate">{group.phone || '-'}</p>
            {group.email && (
              <p className="text-xs text-ink-400 truncate">{group.email}</p>
            )}
          </div>
          {isOwner && (
            <button
              onClick={() => setSubModal({ type: 'edit-info' })}
              className="flex-shrink-0 ml-2 text-xs text-brand-600 hover:text-brand-800 border border-brand-200 rounded px-2 py-1"
            >
              정보 수정
            </button>
          )}
        </div>

        {/* 역할 목록 */}
        <div>
          <p className="text-xs font-medium text-ink-500 mb-2">등록된 역할</p>
          {group.contacts.length === 0 ? (
            <p className="text-sm text-ink-400 py-2">등록된 역할이 없습니다.</p>
          ) : (
            <ul className="divide-y divide-line-soft">
              {group.contacts.map((c) => {
                const dept = c.department || '';
                const pos = c.position || '';
                const label = [dept, pos].filter(Boolean).join(' / ') || '(역할 미지정)';
                return (
                  <li key={c.id} className="py-2 flex items-center justify-between gap-2">
                    <span className="text-sm text-ink-900 flex-1 min-w-0 truncate">{label}</span>
                    {isOwner && (
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() => setSubModal({ type: 'edit-role', contact: c })}
                          className="text-xs text-brand-600 hover:text-brand-800 px-2 py-1 border border-brand-200 rounded"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => setSubModal({ type: 'delete-role', contact: c })}
                          className="text-xs text-red-600 hover:text-red-800 px-2 py-1 border border-red-200 rounded"
                        >
                          삭제
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Modal>

      {/* sub modal: 역할 추가 */}
      {subModal?.type === 'add-role' && (
        <RoleForm
          title="역할 추가"
          departmentSuggestions={departmentSuggestions}
          onSubmit={handleAddRole}
          onCancel={() => setSubModal(null)}
        />
      )}

      {/* sub modal: 역할 수정 */}
      {subModal?.type === 'edit-role' && (
        <RoleForm
          title="역할 수정"
          isEdit
          initialDepartment={subModal.contact.department ?? ''}
          initialPosition={subModal.contact.position ?? ''}
          departmentSuggestions={departmentSuggestions}
          onSubmit={handleEditRole}
          onCancel={() => setSubModal(null)}
        />
      )}

      {/* sub modal: 인적 정보 수정 (역할 필드 숨김, z-[80]) */}
      {subModal?.type === 'edit-info' && (
        <SiteContactFormModal
          open
          title="인적 정보 수정"
          initialValue={{
            name: group.name,
            phone: group.phone,
            department: '',
            position: '',
            email: group.email ?? '',
          }}
          departmentSuggestions={[]}
          requirePhone
          hideRole
          zIndexClass="z-[80]"
          onSubmit={handleEditInfo}
          onCancel={() => setSubModal(null)}
        />
      )}

      {/* sub modal: 역할 삭제 확인 */}
      {subModal?.type === 'delete-role' && (
        <DeleteConfirm
          contact={subModal.contact}
          isDeleting={isDeleting}
          onConfirm={handleDeleteRole}
          onCancel={() => setSubModal(null)}
        />
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// 메인 섹션
// ──────────────────────────────────────────────────────────────────────────────
export function SiteContactsSection({
  companyId,
  siteId,
  isOwner,
}: SiteContactsSectionProps) {
  const queryClient = useQueryClient();
  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['site-contacts', companyId, siteId],
    queryFn: () => getSiteContacts(companyId, siteId),
    enabled: !!companyId && !!siteId,
  });

  // Member 단위 그룹핑 (활성 contacts만 — getSiteContacts가 isActive=true 필터)
  const memberGroups = useMemo(() => groupByMember(contacts), [contacts]);

  // 선택된 Member 그룹 (상세 모달)
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const selectedGroup = useMemo(
    () => memberGroups.find((g) => g.memberId === selectedMemberId) ?? null,
    [memberGroups, selectedMemberId],
  );

  // 새 담당자 추가 폼 (외부 신규 Member 생성 흐름)
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const siteContactCreator = useSiteContactCreator();
  const { showToast } = useToastStore();

  const departmentSuggestions = useMemo(
    () =>
      Array.from(
        new Set(
          contacts
            .map((c) => c.department)
            .filter((d): d is string => Boolean(d && d.trim())),
        ),
      ),
    [contacts],
  );

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['site-contacts', companyId, siteId] });
  };

  const handleNewContactSubmit = async (value: SiteContactFormValue) => {
    const created = await siteContactCreator.create(companyId, siteId, {
      name: value.name,
      phone: value.phone,
      department: value.department,
      position: value.position,
      email: value.email,
    });
    if (created) {
      refresh();
      setShowNewContactForm(false);
    }
  };

  // 실패를 삼키면 충돌 모달이 그대로 떠 있어 "눌러도 아무 일도 안 일어나는" 상태가 된다.
  // 일일안전점검일지 경로(new-daily-inspection-log)와 같은 처리 — 토스트로 알리고 모달은 유지해
  // 다른 선택지로 다시 시도할 수 있게 둔다.
  const handleConflictSelectExisting = async (memberId: string) => {
    try {
      const created = await siteContactCreator.selectExisting(companyId, siteId, memberId);
      if (created) {
        refresh();
        setShowNewContactForm(false);
      }
    } catch (err) {
      showToast(extractApiError(err)?.message ?? '담당자 등록에 실패했습니다.', 'error');
    }
  };

  // 이메일만 겹친 경우 — 대표 메일 공유라 다른 사람일 수 있다
  const handleConflictCreateAsDifferent = async () => {
    try {
      const created = await siteContactCreator.createAsDifferentPerson(companyId, siteId);
      if (created) {
        refresh();
        setShowNewContactForm(false);
      }
    } catch (err) {
      showToast(extractApiError(err)?.message ?? '담당자 등록에 실패했습니다.', 'error');
    }
  };

  return (
    <Card padding="xl" radius="lg" elevation="md" data-testid="site-contacts-section">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-medium text-ink-900">현장 담당자</h2>
          <p className="text-xs text-ink-400 mt-1">보고서·TBM에 등장하는 현장 소속 담당자 목록</p>
        </div>
        {isOwner && (
          <button
            onClick={() => setShowNewContactForm(true)}
            className="text-sm text-brand-600 hover:text-brand-800"
          >
            + 추가
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
        </div>
      ) : memberGroups.length === 0 ? (
        <div className="text-center py-3">
          <svg className="mx-auto h-8 w-8 text-ink-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="mt-2 text-sm text-ink-500">등록된 현장 담당자가 없습니다.</p>
          {isOwner && (
            <button
              onClick={() => setShowNewContactForm(true)}
              className="mt-2 text-sm text-brand-600 hover:text-brand-800"
            >
              담당자 추가하기
            </button>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-line max-h-48 overflow-y-auto overflow-x-hidden">
          {memberGroups.map((group) => (
            <li
              key={group.memberId}
              className="py-3 flex items-center cursor-pointer hover:bg-surface-hover -mx-2 px-2 rounded-card transition-colors"
              onClick={() => setSelectedMemberId(group.memberId)}
            >
              <div className="flex-shrink-0 h-9 w-9 rounded-full bg-green-100 flex items-center justify-center">
                <span className="text-green-700 font-medium text-sm">
                  {group.name.charAt(0)}
                </span>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-ink-900 truncate">{group.name}</p>
                <p className="text-xs text-ink-500 truncate">
                  {group.phone && <span>{group.phone} · </span>}
                  <span className="text-ink-400">{buildRoleSummary(group.contacts)}</span>
                </p>
              </div>
              <ChevronRightIcon className="flex-shrink-0 h-5 w-5 text-ink-400 ml-2" />
            </li>
          ))}
        </ul>
      )}

      {/* Member 상세 모달 */}
      {selectedGroup && (
        <MemberDetailModal
          group={selectedGroup}
          isOwner={isOwner}
          companyId={companyId}
          siteId={siteId}
          departmentSuggestions={departmentSuggestions}
          onClose={() => setSelectedMemberId(null)}
          onRefresh={refresh}
        />
      )}

      {/* 새 담당자 추가 폼 (외부 신규 Member 생성) */}
      <SiteContactFormModal
        open={showNewContactForm}
        title="새 담당자 추가"
        departmentSuggestions={departmentSuggestions}
        onSubmit={handleNewContactSubmit}
        onCancel={() => setShowNewContactForm(false)}
      />

      {/* phone 충돌 시 sub modal */}
      {siteContactCreator.conflict && (
        <MemberPhoneConflictModal
          isOpen
          existingMember={siteContactCreator.conflict.existingMember}
          pendingInput={siteContactCreator.conflict.pendingInput}
          matchedBy={siteContactCreator.conflict.matchedBy}
          isProcessing={siteContactCreator.isCreating}
          onSelectExisting={handleConflictSelectExisting}
          onCreateAsDifferentPerson={handleConflictCreateAsDifferent}
          onCancel={siteContactCreator.cancelConflict}
        />
      )}
    </Card>
  );
}
